import { SellerAccountStatus } from "@/Apollo/schema";

/**
 * Dutch source copy for the Payment settings page (spec v3, approved
 * 2026-10-01). English is the Lokalise upload pair. The status card's title
 * and explanation come pre-localised from the gateway (`status_title`,
 * `status_description`); everything the page adds around them lives here, so
 * one test can enforce the wording rules: no em or en dashes, never a
 * promised arrival date, no Stripe codes, Stripe named as
 * betaalserviceprovider Stripe.
 */
export const PAYMENT_COPY = {
  pageTitle: { nl: "Betaalinstellingen", en: "Payment settings" },

  // Status pills
  pillNotSetUp: { nl: "Niet ingesteld", en: "Not set up" },
  pillReady: { nl: "Klaar voor uitbetaling", en: "Ready for payout" },
  pillDetailsNeeded: { nl: "Gegevens nodig", en: "Details needed" },
  pillActionNeeded: { nl: "Actie nodig", en: "Action needed" },
  pillBeingChecked: { nl: "Wordt gecontroleerd", en: "Being checked" },
  pillBlocked: { nl: "Geblokkeerd", en: "Blocked" },
  pillFetching: { nl: "Status ophalen", en: "Fetching status" },

  // Status card
  updatedJustNow: { nl: "Zojuist bijgewerkt", en: "Updated just now" },
  updatedMinutesAgo: {
    nl: "{{minutes}} min geleden bijgewerkt",
    en: "Updated {{minutes}} min ago",
  },
  updatedOn: { nl: "Bijgewerkt op {{date}}", en: "Updated {{date}}" },
  checkAgain: { nl: "Opnieuw controleren", en: "Check again" },
  fetchingTitle: {
    nl: "Bedankt. We halen je nieuwe status op bij betaalserviceprovider Stripe",
    en: "Thanks. We're fetching your new status from payment service provider Stripe",
  },
  fetchingBody: {
    nl: "Dit duurt een paar seconden.",
    en: "This takes a few seconds.",
  },
  fetchingTimedOut: {
    nl: "We hebben je gegevens ontvangen. Betaalserviceprovider Stripe controleert ze. We mailen je zodra het klaar is.",
    en: "We've received your details. Payment service provider Stripe is checking them. We'll email you when it's done.",
  },
  amountWaitingForYou: {
    nl: "{{amount}} wacht op je",
    en: "{{amount}} is waiting for you",
  },
  amountWaitingForCheck: {
    nl: "{{amount}} wacht op de controle",
    en: "{{amount}} is waiting for the check",
  },
  amountWaiting: { nl: "{{amount}} wacht", en: "{{amount}} is waiting" },
  deadlineBefore: {
    nl: "Doe dit vóór {{date}}. Daarna pauzeert betaalserviceprovider Stripe je uitbetalingen.",
    en: "Do this before {{date}}. After that, payment service provider Stripe pauses your payouts.",
  },
  reviewSlow: {
    nl: "Dit duurt langer dan normaal. Dat gebeurt soms. Is het na zeven werkdagen nog niet klaar, neem dan contact met ons op.",
    en: "This is taking longer than usual. That happens sometimes. If it's still not done after seven business days, contact us.",
  },
  reviewSlowContact: {
    nl: "<support>Neem contact op met de klantenservice</support>",
    en: "<support>Contact customer service</support>",
  },
  noListingYet: {
    nl: "Nog geen item te koop? Je betaalaccount wordt aangemaakt zodra je eerste item online staat. Je kunt je bankrekening hieronder alvast toevoegen.",
    en: "No item for sale yet? Your payment account is created as soon as your first item is online. You can already add your bank account below.",
  },

  // Buttons
  buttonSetUp: { nl: "Uitbetalingen instellen", en: "Set up payouts" },
  buttonViewOrEdit: {
    nl: "Gegevens bekijken of wijzigen",
    en: "View or edit your details",
  },
  buttonViewDetails: { nl: "Gegevens bekijken", en: "View your details" },
  buttonContinueAtStripe: { nl: "Verder bij Stripe", en: "Continue at Stripe" },
  buttonContactUs: { nl: "Contact opnemen", en: "Contact us" },
  buttonCheckAddress: { nl: "Adres controleren", en: "Check your address" },

  // Sections
  sectionStripeAsks: {
    nl: "Wat Stripe van je vraagt",
    en: "What Stripe will ask you for",
  },
  sectionBeingChecked: { nl: "Wordt gecontroleerd", en: "Being checked" },
  sectionWaiting: { nl: "Wat er wacht", en: "What is waiting" },
  sectionBankAccount: { nl: "Bankrekening", en: "Bank account" },
  sectionRecentPayouts: { nl: "Recente uitbetalingen", en: "Recent payouts" },
  sectionQuestions: { nl: "Vragen", en: "Questions" },
  onThisPage: { nl: "Op deze pagina", en: "On this page" },

  // Checklist before onboarding (status A)
  expectDetailsTitle: {
    nl: "Je naam, geboortedatum, adres en telefoonnummer",
    en: "Your name, date of birth, address and phone number",
  },
  expectDetailsWhy: {
    nl: "Precies zoals op je identiteitsbewijs. Stripe vergelijkt ze met officiële registers.",
    en: "Exactly as on your ID. Stripe compares them with official records.",
  },
  expectIdTitle: {
    nl: "Soms een foto van je identiteitsbewijs",
    en: "Sometimes a photo of your ID",
  },
  expectIdWhy: {
    nl: "Alleen als je gegevens niet automatisch gekoppeld kunnen worden, of zodra je boven een bepaald bedrag hebt verkocht.",
    en: "Only if your details can't be matched automatically, or once you've sold above a certain amount.",
  },
  bankTaskTitle: { nl: "Je bankrekening", en: "Your bank account" },
  bankTaskWhy: {
    nl: "De rekening waar je uitbetalingen naartoe gaan. Hij moet op jouw naam staan.",
    en: "The account your payouts go to. It has to be in your name.",
  },

  // Under review (status E)
  reviewDocumentTitle: { nl: "Je identiteitsdocument", en: "Your ID document" },
  reviewSubmittedOn: {
    nl: "Ingediend op {{date}}. Betaalserviceprovider Stripe beoordeelt het.",
    en: "Submitted on {{date}}. Payment service provider Stripe is reviewing it.",
  },
  reviewSince: {
    nl: "In beoordeling bij betaalserviceprovider Stripe sinds {{date}}.",
    en: "Under review at payment service provider Stripe since {{date}}.",
  },
  reviewDetailsTitle: { nl: "Je gegevens", en: "Your details" },
  reviewInProgress: {
    nl: "Betaalserviceprovider Stripe beoordeelt je gegevens.",
    en: "Payment service provider Stripe is reviewing your details.",
  },

  // Money breakdown
  groupAccount: { nl: "Wacht op je account", en: "Waiting for your account" },
  groupCheck: { nl: "Wacht op de controle", en: "Waiting for the check" },
  groupOther: { nl: "Wacht op iets anders", en: "Waiting for something else" },
  balanceLine: {
    nl: "Saldo bij betaalserviceprovider Stripe",
    en: "Balance at payment service provider Stripe",
  },
  balanceReleaseAccount: {
    nl: "Al bij Stripe · gaat automatisch naar je bank zodra je account is vrijgegeven",
    en: "Already with Stripe · goes to your bank automatically once your account is released",
  },
  balanceReleaseCheck: {
    nl: "Al bij Stripe · gaat automatisch naar je bank zodra de controle klaar is",
    en: "Already with Stripe · goes to your bank automatically once the check is done",
  },
  balanceReleaseResolved: {
    nl: "Al bij Stripe · gaat automatisch naar je bank zodra dit is opgelost",
    en: "Already with Stripe · goes to your bank automatically once this is resolved",
  },
  orderReleaseAccount: {
    nl: "Verkocht op {{date}} · we maken het over zodra je account is vrijgegeven",
    en: "Sold {{date}} · we transfer it once your account is released",
  },
  orderReleaseCheck: {
    nl: "Verkocht op {{date}} · we maken het over zodra de controle klaar is",
    en: "Sold {{date}} · we transfer it once the check is done",
  },
  orderReleaseResolved: {
    nl: "Verkocht op {{date}} · we maken het over zodra dit is opgelost",
    en: "Sold {{date}} · we transfer it as soon as this is resolved",
  },
  tagDispute: { nl: "Geschil met koper", en: "Dispute with buyer" },
  tagReturn: { nl: "Retour in behandeling", en: "Return in progress" },
  tagDamage: {
    nl: "Schadeclaim in behandeling",
    en: "Damage claim in progress",
  },
  tagRefund: { nl: "Terugbetaling in behandeling", en: "Refund in progress" },
  otherRelease: {
    nl: "Wordt uitbetaald zodra dit is opgelost, ongeacht de status van je account.",
    en: "Paid out once this is resolved, whatever the state of your account.",
  },
  viewConversation: { nl: "Bekijk het gesprek", en: "View the conversation" },

  // Bank account
  bankChange: { nl: "Wijzigen", en: "Change" },
  bankCancel: { nl: "Annuleren", en: "Cancel" },
  bankAdd: { nl: "Bankrekening toevoegen", en: "Add your bank account" },
  bankSave: { nl: "Bankrekening opslaan", en: "Save bank account" },
  bankNone: { nl: "Nog geen bankrekening", en: "No bank account yet" },
  bankSavedAtWhoppah: {
    nl: "Opgeslagen bij Whoppah. We geven je rekening door aan betaalserviceprovider Stripe zodra je betaalaccount is aangemaakt.",
    en: "Saved at Whoppah. We pass your account on to payment service provider Stripe as soon as your payment account is created.",
  },
  bankErrored: {
    nl: "Een uitbetaling naar deze rekening is mislukt. Controleer je IBAN en pas hem aan.",
    en: "A payout to this account failed. Check your IBAN and update it.",
  },

  // Payouts
  payoutsEmpty: { nl: "Nog geen uitbetalingen.", en: "No payouts yet." },

  // Help and errors
  helpLine: {
    nl: "Kom je er niet uit? <support>Neem contact op met de klantenservice</support>",
    en: "Stuck? <support>Contact customer service</support>",
  },
  linkError: {
    nl: "Het lukte niet om de pagina van Stripe te openen. Probeer het opnieuw.",
    en: "We couldn't open the Stripe page. Please try again.",
  },

  // Reason lines for a rejected document
  reasonNotReadable: {
    nl: "De foto was wazig of te donker.",
    en: "The photo was blurry or too dark.",
  },
  reasonExpired: {
    nl: "Het document is verlopen.",
    en: "The document has expired.",
  },
  reasonTooLarge: {
    nl: "Het bestand was te groot.",
    en: "The file was too large.",
  },
  reasonTypeNotSupported: {
    nl: "Dit documenttype wordt niet geaccepteerd. Gebruik een paspoort, ID-kaart of rijbewijs.",
    en: "This document type isn't accepted. Use a passport, ID card or driving licence.",
  },
  reasonMissingSide: {
    nl: "Eén kant van het document ontbreekt.",
    en: "One side of the document is missing.",
  },
  reasonCopy: {
    nl: "Een kopie of zwart-witscan wordt niet geaccepteerd. Maak een kleurenfoto van het origineel.",
    en: "A copy or black-and-white scan isn't accepted. Take a colour photo of the original.",
  },

  // Questions per status (three each, from what Maison is asked)
  qSetupHowLong: { nl: "Hoe lang duurt dit?", en: "How long does this take?" },
  aSetupHowLong: {
    nl: "Ongeveer vijf minuten. Houd je identiteitsbewijs en je IBAN bij de hand.",
    en: "About five minutes. Have your ID and your IBAN ready.",
  },
  qSetupSellBefore: {
    nl: "Kan ik al verkopen voordat dit klaar is?",
    en: "Can I sell before this is done?",
  },
  aSetupSellBefore: {
    nl: "Ja. Je items blijven online en kopers kunnen betalen. We kunnen je pas uitbetalen als dit is afgerond.",
    en: "Yes. Your items stay online and buyers can pay. We can only pay you out once this is complete.",
  },
  qWhyNotWhoppah: {
    nl: "Waarom betaalt Whoppah me niet gewoon zelf?",
    en: "Why doesn't Whoppah just pay me?",
  },
  qWhyNotDirect: {
    nl: "Waarom betaalt Whoppah me niet rechtstreeks?",
    en: "Why doesn't Whoppah pay me directly?",
  },
  aWhyNotWhoppah: {
    nl: "Als marktplaats mogen we geld van kopers niet zelf vasthouden of doorgeven. Dat moet een vergunde betaaldienstverlener doen. Voor Whoppah is dat betaalserviceprovider Stripe, onder toezicht van De Nederlandsche Bank en de AFM.",
    en: "As a marketplace we may not hold or forward buyers' money ourselves. A licensed payment institution has to do that. For Whoppah that is payment service provider Stripe, supervised by De Nederlandsche Bank and the AFM.",
  },
  qReadyWhenMoney: {
    nl: "Wanneer ontvang ik mijn geld na een verkoop?",
    en: "When do I receive my money after a sale?",
  },
  aReadyWhenMoney: {
    nl: "We maken het over zodra de koper bevestigt dat het item is aangekomen, of automatisch tien dagen na bezorging. Betaalserviceprovider Stripe betaalt het daarna volgens zijn eigen schema uit naar je bank.",
    en: "We transfer it as soon as the buyer confirms the item arrived, or automatically ten days after delivery. Payment service provider Stripe then pays it to your bank on its own schedule.",
  },
  qReadyChangeBank: {
    nl: "Hoe wijzig ik mijn bankrekening?",
    en: "How do I change my bank account?",
  },
  aReadyChangeBank: {
    nl: "Via Wijzigen hierboven. Uitbetalingen gaan naar de nieuwe rekening zodra Stripe die heeft geaccepteerd.",
    en: "Use Change above. Payouts switch to the new account as soon as Stripe has accepted it.",
  },
  qActiveWhyIdNow: {
    nl: "Waarom heeft Stripe nu mijn identiteitsbewijs nodig?",
    en: "Why does Stripe need my ID now?",
  },
  aActiveWhyIdNow: {
    nl: "Vanaf een bepaald verkoopvolume verplicht de wet betaalserviceprovider Stripe om de identiteit van de persoon achter het account te controleren met een identiteitsbewijs. Je gegevens zijn al goedgekeurd; dit is de laatste stap.",
    en: "Above a certain sales volume, the law requires payment service provider Stripe to verify the identity of the person behind the account with an ID document. Your details were already approved; this is the final step.",
  },
  qActiveMissDeadline: {
    nl: "Wat gebeurt er als ik dit niet vóór de datum doe?",
    en: "What happens if I don't do this before the date?",
  },
  aActiveMissDeadline: {
    nl: "Stripe pauzeert je uitbetalingen tot het document is goedgekeurd. Geld van verkopen blijft veilig in je Stripe-saldo en wordt uitbetaald zodra je klaar bent.",
    en: "Stripe pauses your payouts until the document is approved. Money from sales stays safe in your Stripe balance and is paid out once you're done.",
  },
  qDocumentSafe: { nl: "Is mijn document veilig?", en: "Is my document safe?" },
  aDocumentSafe: {
    nl: "Je document gaat rechtstreeks naar Stripe via een beveiligde verbinding. Whoppah ontvangt, bewaart of ziet het niet. Stripe accepteert geen documenten per e-mail.",
    en: "Your document goes directly to Stripe over an encrypted connection. Whoppah does not receive, store or see it. Stripe does not accept documents by email.",
  },
  qBlockedWhyId: {
    nl: "Waarom heeft Stripe mijn identiteitsbewijs nodig?",
    en: "Why does Stripe need my ID?",
  },
  aBlockedWhyId: {
    nl: "De wet verplicht betaalserviceprovider Stripe om te controleren aan wie het uitbetaalt. Als je ingevulde gegevens niet aan officiële registers gekoppeld konden worden, is een foto van je identiteitsbewijs de manier om ze te bevestigen.",
    en: "The law requires payment service provider Stripe to verify who it pays out to. If your typed details could not be matched to official records, a photo of your ID is the way to confirm them.",
  },
  qCheckHowLong: {
    nl: "Hoe lang duurt de controle?",
    en: "How long does the check take?",
  },
  aCheckHowLong: {
    nl: "Meestal minuten, soms enkele werkdagen. We mailen je zodra het klaar is en betalen automatisch uit.",
    en: "Usually minutes, sometimes a few business days. We email you when it's done and pay out automatically.",
  },
  qWhereMoneyNow: {
    nl: "Waar is mijn geld nu?",
    en: "Where is my money right now?",
  },
  aWhereMoneyAccount: {
    nl: "Veilig. Verkopen die we nog niet hebben overgemaakt houdt Whoppah vast. Geld dat al is overgemaakt staat in je saldo bij Stripe. Beide worden uitbetaald zodra je account is vrijgegeven.",
    en: "Safe. Sales we have not transferred yet are held by Whoppah. Money already transferred sits in your balance at Stripe. Both are paid out once your account is released.",
  },
  aWhereMoneyCheck: {
    nl: "Veilig. Verkopen die we nog niet hebben overgemaakt houdt Whoppah vast. Geld dat al is overgemaakt staat in je saldo bij Stripe. Beide worden uitbetaald zodra de controle klaar is.",
    en: "Safe. Sales we have not transferred yet are held by Whoppah. Money already transferred sits in your balance at Stripe. Both are paid out once the check is done.",
  },
  qReviewSpeedUp: { nl: "Kan ik het versnellen?", en: "Can I speed it up?" },
  aReviewSpeedUp: {
    nl: "Nee. Betaalserviceprovider Stripe doet de controle en wij hebben geen invloed op de wachtrij. Een tweede document uploaden helpt niet en kan het vertragen.",
    en: "No. Payment service provider Stripe does the check and we can't influence its queue. Uploading a second document does not help and can slow things down.",
  },
  qNotFixableWho: { nl: "Wie lost dit op?", en: "Who is fixing this?" },
  aNotFixableWho: {
    nl: "Wij. Je hoeft niets op te sturen.",
    en: "We are. You don't need to send anything.",
  },
  qNotFixableWhereMoney: { nl: "Waar is mijn geld?", en: "Where is my money?" },
  aNotFixableWhereMoney: {
    nl: "Veilig, bij Whoppah of in je saldo bij betaalserviceprovider Stripe. Het wordt uitbetaald zodra dit is opgelost.",
    en: "Safe, held by Whoppah or in your balance at payment service provider Stripe. It is paid out as soon as this is resolved.",
  },
  qNotFixableHear: {
    nl: "Hoor ik nog iets van jullie?",
    en: "Will I hear from you?",
  },
  aNotFixableHear: {
    nl: "Ja, per e-mail. Heb je binnen vijf werkdagen niets gehoord, neem dan contact op met de klantenservice.",
    en: "Yes, by email. If you haven't heard from us within five business days, contact customer service.",
  },
  qAddressWhyRefused: {
    nl: "Waarom accepteert Stripe mijn adres niet?",
    en: "Why doesn't Stripe accept my address?",
  },
  aAddressWhyRefused: {
    nl: "Betaalserviceprovider Stripe controleert adressen aan de hand van het postcoderegister. Een postcode en straat die niet bij elkaar horen, of een postbus, worden geweigerd.",
    en: "Payment service provider Stripe checks addresses against the postal register. A postcode and street that don't match, or a PO box, are refused.",
  },
  qAddressFormat: {
    nl: "Hoe moet het adres eruitzien?",
    en: "What should the address look like?",
  },
  aAddressFormat: {
    nl: "Je woon- of bedrijfsadres zoals de post het schrijft: straat en huisnummer, postcode, plaats.",
    en: "Your home or business address as the postal service writes it: street and number, postcode, city.",
  },
  qAddressAfterFix: {
    nl: "Wat gebeurt er nadat ik het heb aangepast?",
    en: "What happens after I fix it?",
  },
  aAddressAfterFix: {
    nl: "We sturen je gegevens automatisch opnieuw naar Stripe, meestal binnen een dag, en deze pagina gaat door naar de volgende stap.",
    en: "We send your details to Stripe again automatically, usually within a day, and this page moves on to the next step.",
  },
};

export type PaymentCopyKey = keyof typeof PAYMENT_COPY;

const toKebabCase = (value: string) =>
  value.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();

/**
 * Lokalise key for one copy entry. The settings page loads the `account`
 * namespace, so every key lives there under one prefix.
 */
export const paymentLokaliseKey = (copyKey: PaymentCopyKey): string =>
  `account:payment-settings-${toKebabCase(copyKey)}`;

export const PAYMENT_LOKALISE_KEYS: {
  key: string;
  copyKey: PaymentCopyKey;
}[] = (Object.keys(PAYMENT_COPY) as PaymentCopyKey[]).map(copyKey => ({
  key: paymentLokaliseKey(copyKey),
  copyKey,
}));

export type StatusTone =
  | "grey"
  | "brand"
  | "burst"
  | "attention"
  | "curious"
  | "dark";

export type StatusPresentation = {
  pill: PaymentCopyKey;
  tone: StatusTone;
};

/** Pill copy and tint per status, exhaustive so a new status cannot render untinted. */
export const STATUS_PRESENTATION: Record<
  SellerAccountStatus,
  StatusPresentation
> = {
  [SellerAccountStatus.NotSetUp]: { pill: "pillNotSetUp", tone: "grey" },
  [SellerAccountStatus.Ready]: { pill: "pillReady", tone: "brand" },
  [SellerAccountStatus.ActiveActionNeeded]: {
    pill: "pillDetailsNeeded",
    tone: "burst",
  },
  [SellerAccountStatus.BlockedActionNeeded]: {
    pill: "pillActionNeeded",
    tone: "attention",
  },
  [SellerAccountStatus.UnderReview]: {
    pill: "pillBeingChecked",
    tone: "curious",
  },
  [SellerAccountStatus.BlockedNotFixable]: {
    pill: "pillBlocked",
    tone: "dark",
  },
  [SellerAccountStatus.AccountNotCreated]: {
    pill: "pillActionNeeded",
    tone: "attention",
  },
};

export type StatusQuestion = {
  question: PaymentCopyKey;
  answer: PaymentCopyKey;
};

/** Exactly three questions per status; the copy test enforces the count. */
export const STATUS_QUESTIONS: Record<SellerAccountStatus, StatusQuestion[]> = {
  [SellerAccountStatus.NotSetUp]: [
    { question: "qSetupHowLong", answer: "aSetupHowLong" },
    { question: "qSetupSellBefore", answer: "aSetupSellBefore" },
    { question: "qWhyNotWhoppah", answer: "aWhyNotWhoppah" },
  ],
  [SellerAccountStatus.Ready]: [
    { question: "qReadyWhenMoney", answer: "aReadyWhenMoney" },
    { question: "qWhyNotDirect", answer: "aWhyNotWhoppah" },
    { question: "qReadyChangeBank", answer: "aReadyChangeBank" },
  ],
  [SellerAccountStatus.ActiveActionNeeded]: [
    { question: "qActiveWhyIdNow", answer: "aActiveWhyIdNow" },
    { question: "qActiveMissDeadline", answer: "aActiveMissDeadline" },
    { question: "qDocumentSafe", answer: "aDocumentSafe" },
  ],
  [SellerAccountStatus.BlockedActionNeeded]: [
    { question: "qBlockedWhyId", answer: "aBlockedWhyId" },
    { question: "qCheckHowLong", answer: "aCheckHowLong" },
    { question: "qWhereMoneyNow", answer: "aWhereMoneyAccount" },
  ],
  [SellerAccountStatus.UnderReview]: [
    { question: "qCheckHowLong", answer: "aCheckHowLong" },
    { question: "qReviewSpeedUp", answer: "aReviewSpeedUp" },
    { question: "qWhereMoneyNow", answer: "aWhereMoneyCheck" },
  ],
  [SellerAccountStatus.BlockedNotFixable]: [
    { question: "qNotFixableWho", answer: "aNotFixableWho" },
    { question: "qNotFixableWhereMoney", answer: "aNotFixableWhereMoney" },
    { question: "qNotFixableHear", answer: "aNotFixableHear" },
  ],
  [SellerAccountStatus.AccountNotCreated]: [
    { question: "qAddressWhyRefused", answer: "aAddressWhyRefused" },
    { question: "qAddressFormat", answer: "aAddressFormat" },
    { question: "qAddressAfterFix", answer: "aAddressAfterFix" },
  ],
};

/** Query parameter Stripe's return_url carries; the page polls while it is set. */
export const STRIPE_RETURN_PARAM = "stripe_return";
/** Query parameter Stripe's refresh_url carries; the page mints a new link at once. */
export const STRIPE_REFRESH_PARAM = "stripe_refresh";
/** Query parameter the emails carry; highlights and scrolls to the matching block. */
export const PAYMENT_REASON_PARAM = "reason";

export const STRIPE_RETURN_POLL_MS = 3_000;
/** After the fast minute: keep looking, quietly, every half minute... */
export const STRIPE_RETURN_SLOW_POLL_MS = 30_000;
/** ...for this long in all, then stop and leave the page as the gateway says. */
export const STRIPE_RETURN_GIVE_UP_MS = 10 * 60_000;

/**
 * The gateway re-reads Stripe on open when the mirror is older than this
 * (MIRROR_MAX_AGE_MS in its bank-account service), so anything younger is
 * "just now" to the seller.
 */
export const UPDATED_JUST_NOW_MINUTES = 5;
export const STRIPE_RETURN_MAX_MS = 60_000;

/** The deadline panel turns attention this close to the date. */
export const DEADLINE_URGENT_DAYS = 3;
/** A review older than this many business days gets the "taking longer" panel. */
export const REVIEW_SLOW_BUSINESS_DAYS = 4;
/** After this many business days the slow-review panel links customer service. */
export const REVIEW_CONTACT_BUSINESS_DAYS = 7;
/**
 * `pending_since` is stamped the first time our mirror sees Stripe's review,
 * which can be years after it began. Only a stamp older than the mirror's
 * own read by at least this long is a date worth telling the seller.
 */
export const REVIEW_SINCE_KNOWN_AFTER_MS = 24 * 60 * 60 * 1000;
